import {CheckoutFragment} from '../../../../gql/graphql';
import {Checkout, ModelContext} from '../../../../types/checkoutApp.types';
import {AdditionalFeeModel} from './AdditionalFee.model';
import {ViolationModel} from './Violation.model';
import {PaymentSummaryModel} from './PaymentSummary.model';
import {PriceSummaryModel} from './PriceSummary.model';
import {mapNullToUndefined} from '../../../utils/mapperHelper.utils';
import {ErrorsModel} from '../Errors.model';
import {CartModel} from '../Cart/Cart.model';
import {CouponDiscountModel} from './CouponDiscount.model';
import {TaxSummaryModel} from './TaxSummary.model';
import {PriceModel} from '../Price.model';

export class CartSummaryModel {
  public additionalFees: AdditionalFeeModel[];
  public violations: ViolationModel[];
  public paymentSummary: PaymentSummaryModel;
  public priceSummary: PriceSummaryModel;
  public taxSummary: TaxSummaryModel;
  public errors: ErrorsModel;
  public couponDiscount?: CouponDiscountModel;
  public hasTax: boolean;

  constructor(params: CartSummaryModel) {
    this.additionalFees = params.additionalFees;
    this.violations = params.violations;
    this.paymentSummary = params.paymentSummary;
    this.priceSummary = params.priceSummary;
    this.taxSummary = params.taxSummary;
    this.errors = params.errors;
    this.couponDiscount = params.couponDiscount;
    this.hasTax = params.hasTax;
  }

  public static fromCheckoutV1(checkout: CheckoutFragment, cartModel: CartModel, modelContext: ModelContext) {
    const tax = PriceModel.fromCheckoutV1(checkout.priceSummary?.tax, modelContext);
    return new CartSummaryModel({
      additionalFees: (checkout.additionalFees ?? [])
        .filter((fee) => !!fee)
        .map((additionalFee) => AdditionalFeeModel.fromCheckoutV1(additionalFee, modelContext)),
      violations: mapViolations(checkout),
      paymentSummary: PaymentSummaryModel.fromCheckoutV1({
        requiresPaymentAfterGiftCard: checkout.paymentRequired,
        totalAfterGiftCards: checkout.totalAfterGiftCard,
        payNow: checkout.payNowTotalAfterGiftCard,
        payLater: checkout.payLater,
        payAfterFreeTrial: checkout.payAfterFreeTrial,
        giftCard: checkout.giftCard,
        subscriptionCharges: checkout.subscriptionCharges,
        lineItems: cartModel.lineItems,
        modelContext,
      }),
      priceSummary: PriceSummaryModel.fromCheckoutV1({
        priceSummary: mapNullToUndefined(checkout.priceSummary),
        deliveryTotalPriceAfterTax: checkout.shippingInfo?.selectedCarrierServiceOption?.cost?.totalPriceAfterTax,
        modelContext,
      }),
      taxSummary: TaxSummaryModel.fromCheckoutV1({
        taxAmount: checkout.priceSummary?.tax,
        aggregatedTaxBreakdown: checkout.taxSummary?.aggregatedTaxBreakdown,
        modelContext,
      }),
      couponDiscount: CouponDiscountModel.fromCheckoutV1(
        checkout.appliedDiscounts ?? [],
        modelContext,
        checkout.priceSummary?.discount
      ),
      errors: new ErrorsModel(mapNullToUndefined(checkout.calculationErrors)),
      hasTax: tax.amount > 0,
    });
  }
}

function mapViolations(checkout: Checkout): ViolationModel[] {
  return (checkout.violations ?? /* istanbul ignore next */ [])
    .filter((violation) => Boolean(violation))
    .map((violation) => ViolationModel.fromCheckoutV1(violation!));
}
